/* eslint-disable complexity */
import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Input } from 'src/@vymo/ui/atoms';
import classNames from 'classnames';
import { format as formatDate, parse } from 'date-fns';
import { ReactComponent as ClockIcon } from 'src/assets/icons/clock.svg';
import Logger from 'src/logger';
import { getTimePickerColumns } from '../rangePicker/queries';
import {
  clampDateTimeToBounds,
  getDateFromTime,
  isDateTimeWithinBounds,
  isHourWithinBounds,
} from './queries';
import TimeList from './timeList';
import { TimeFormat, TimePickerProps } from './types';
import styles from './index.module.scss';

const log = new Logger('TimePicker');

function TimePicker({
  value = '',
  onChange,
  format,
  showInput = true,
  step = { stepHours: 1, stepMinutes: 5, stepSeconds: 5 },
  discreteTimeOptions = { hours: null, minutes: null, seconds: null },
  date = null,
  minTime,
  maxTime,
  'data-test-id': dataTestId,
  viewMode = false,
}: TimePickerProps) {
  format = (format || 'HH:mm') as TimeFormat;

  const [hours, setHoursState] = useState<number | null>(null);
  const [minutes, setMinutesState] = useState<number | null>(null);
  const [seconds, setSecondsState] = useState<number | null>(null);
  const [period, setPeriod] = useState<string | null>(null);
  const inputRef = useRef() as MutableRefObject<any>;
  const [showTimePicker, setShowTimePicker] = useState(!showInput);
  const [timeValue, setTimeValue] = useState(value);

  const show12Hours = format.indexOf('a') > -1;
  const { showHours, showMinutes, showSeconds } = getTimePickerColumns(format);
  const { stepHours = 1, stepMinutes = 5, stepSeconds = 5 } = step;
  const hourOptions =
    discreteTimeOptions?.hours ??
    (show12Hours ? [...Array(13).keys()].slice(1, 13) : [...Array(24).keys()]);
  const minuteOptions = discreteTimeOptions?.minutes ?? [...Array(60).keys()];
  const secondOptions = discreteTimeOptions?.seconds ?? [...Array(60).keys()];
  const referenceDay = useMemo(
    () => date ?? new Date(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [date?.getTime()],
  );

  useEffect(() => {
    setTimeValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showInput &&
        inputRef?.current &&
        !inputRef?.current.contains(e.target)
      ) {
        setShowTimePicker(false);
        setTimeValue(value);
      }
    };
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, [inputRef, showInput, value]);

  useEffect(() => {
    if (timeValue) {
      const initialTime = parse(timeValue, format as string, referenceDay);
      setHoursState(
        show12Hours
          ? initialTime.getHours() % 12 || 12
          : initialTime.getHours(),
      );
      setMinutesState(initialTime.getMinutes());
      setSecondsState(initialTime.getSeconds());
      setPeriod(initialTime.getHours() >= 12 ? 'PM' : 'AM');
    } else {
      setHoursState(null);
      setMinutesState(null);
      setSecondsState(null);
      setPeriod(null);
    }
  }, [timeValue, show12Hours, format, referenceDay]);

  const commitTime = useCallback(
    (
      nextHours: number | null,
      nextMinutes: number | null,
      nextSeconds: number | null,
      nextPeriod: string | null,
    ) => {
      if (nextHours == null) {
        return;
      }

      const candidate = getDateFromTime(
        show12Hours,
        nextHours,
        nextMinutes,
        nextSeconds,
        nextPeriod,
        referenceDay,
      );
      const bounded = clampDateTimeToBounds(candidate, minTime, maxTime);

      setHoursState(
        show12Hours ? bounded.getHours() % 12 || 12 : bounded.getHours(),
      );
      setMinutesState(bounded.getMinutes());
      setSecondsState(bounded.getSeconds());
      setPeriod(bounded.getHours() >= 12 ? 'PM' : 'AM');
      const formattedValue = formatDate(bounded, format as string);
      setTimeValue(formattedValue);
      onChange(formattedValue, null, bounded);
    },
    [show12Hours, referenceDay, minTime, maxTime, format, onChange],
  );

  const isMinuteAllowed = useCallback(
    (minute: number) => {
      if (hours == null) {
        return true;
      }

      const candidate = getDateFromTime(
        show12Hours,
        hours,
        minute,
        seconds,
        period,
        referenceDay,
      );

      if (isDateTimeWithinBounds(candidate, minTime, maxTime)) {
        return true;
      }

      return [minTime, maxTime].some(
        (bound) => bound && bound.getMinutes() === minute,
      );
    },
    [hours, seconds, period, show12Hours, referenceDay, minTime, maxTime],
  );

  const handleHoursChange = (hourValue: number | string) => {
    commitTime(Number(hourValue), minutes, seconds, period);
  };

  const handleMinutesChange = (minValue: number | string) => {
    commitTime(hours, Number(minValue), seconds, period);
  };

  const handleSecondsChange = (secondsValue: number | string) => {
    commitTime(hours, minutes, Number(secondsValue), period);
  };

  const handlePeriodChange = (periodValue: string) => {
    commitTime(hours, minutes, seconds, periodValue);
  };

  const handleOnChange = (inputValue: string) => {
    setTimeValue(inputValue);
    if (!inputValue) {
      onChange(null, null, null);
      return;
    }
    try {
      const parsed = parse(inputValue, format as string, referenceDay);
      commitTime(
        show12Hours ? parsed.getHours() % 12 || 12 : parsed.getHours(),
        parsed.getMinutes(),
        parsed.getSeconds(),
        parsed.getHours() >= 12 ? 'PM' : 'AM',
      );
    } catch (error) {
      log.info(error);
    }
  };

  const timePickerClasses = classNames(styles.timePicker, {
    [styles.timePicker__absolutePosition]: showInput,
  });

  return (
    <div className={styles.timePicker__wrapper}>
      {showInput && (
        <Input
          ref={inputRef}
          iconRight={<ClockIcon />}
          onClick={() => setShowTimePicker(true)}
          value={timeValue}
          onChange={handleOnChange}
          clearInputIcon
          data-test-id={dataTestId}
          viewMode={viewMode}
          variant="block"
          onTouchStart={() => setShowTimePicker(true)}
        />
      )}
      {showTimePicker && (
        <div className={timePickerClasses}>
          <div className={styles.timePicker__timeValue}>{timeValue}</div>
          <div className={styles.timePicker__content}>
            {showHours && (
              <TimeList
                value={hours}
                onItemClick={handleHoursChange}
                options={hourOptions
                  .filter((num) => num % stepHours === 0)
                  .filter(
                    (hour) =>
                      !minTime ||
                      !maxTime ||
                      isHourWithinBounds(
                        referenceDay,
                        show12Hours,
                        hour,
                        period,
                        minTime,
                        maxTime,
                        stepMinutes,
                      ),
                  )
                  .map((hour) => ({
                    label: hour < 10 ? `0${hour}` : hour,
                    value: hour,
                  }))}
              />
            )}
            {showMinutes && (
              <TimeList
                value={minutes}
                onItemClick={handleMinutesChange}
                options={minuteOptions
                  .filter((num) => num % stepMinutes === 0)
                  .filter((minute) => isMinuteAllowed(minute))
                  .map((minute) => ({
                    label: minute < 10 ? `0${minute}` : minute,
                    value: minute,
                  }))}
              />
            )}
            {showSeconds && (
              <TimeList
                value={seconds}
                onItemClick={handleSecondsChange}
                options={secondOptions
                  .filter((num) => num % stepSeconds === 0)
                  .map((second) => ({
                    label: second < 10 ? `0${second}` : second,
                    value: second,
                  }))}
              />
            )}
            {show12Hours && (
              <TimeList
                value={period}
                onItemClick={handlePeriodChange}
                options={[
                  { label: 'AM', value: 'AM' },
                  { label: 'PM', value: 'PM' },
                ]}
              />
            )}
          </div>
          {showInput && (
            <div className={styles.timePicker__footer}>
              <Button
                type="text"
                onClick={() => {
                  const now = new Date();
                  commitTime(
                    show12Hours ? now.getHours() % 12 || 12 : now.getHours(),
                    now.getMinutes(),
                    now.getSeconds(),
                    now.getHours() >= 12 ? 'PM' : 'AM',
                  );
                }}
              >
                Now
              </Button>
              <Button onClick={() => setShowTimePicker(false)}>Ok</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TimePicker;
